import { useState } from 'react';
import { SquareUser, ShoppingCart, Star, IdCard } from 'lucide-react';

import ListCartsForCardPage from '@/app/cart/ListCartsForCardPage';

import Page from '@/components/layout/Page';
import HorizontalLogo from '@/components/common/HorizontalLogo';
import Error from '@/components/common/Error';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import PageLoading from '@/components/layout/PageLoading';
import { cn } from '@/utils';
import {
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription
} from '@/components/ui/empty';

import { useCard, usePutCard } from '@/data/card';
import { useCoachSelf } from '@/data/coach';
import { useActivities } from '@/data/activity';

import { CARD_MODES } from './enums';

const CardPage = () => {
  const card = useCard();
  const putCard = usePutCard();
  const coach = useCoachSelf();
  const activities = useActivities();
  // eslint-disable-next-line no-unused-vars
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  if (card.isLoading && !card.data) {
    return <PageLoading />;
  }

  if (coach.isLoading && !coach.data) {
    return <PageLoading />;
  }

  if (activities.isLoading && !activities.data) {
    return <PageLoading />;
  }

  const handleSelectMode = async (mode) => {
    try {
      setSubmitting(mode);
      await card.mutate(putCard({ mode }), {
        optimisticData: (current) => ({ ...current, mode }),
        rollbackOnError: true,
        populateCache: true,
        revalidate: false
      });
    } catch (error) {
      setApiError(error.message);
    } finally {
      setSubmitting(null);
    }
  };

  if (!card.data) {
    return (
      <Page>
        <div className="p-8">
          <HorizontalLogo />
        </div>
        <Card>
          <CardHeader>
            <CardTitle>ProFound Card</CardTitle>
          </CardHeader>
          <CardContent>
            <Empty>
              <EmptyIcon>
                <IdCard />
              </EmptyIcon>
              <EmptyTitle>No Card Found</EmptyTitle>
              <EmptyDescription>
                Please contact support to get your card
              </EmptyDescription>
            </Empty>
          </CardContent>
        </Card>
        <div className="p-12"></div>
      </Page>
    );
  }

  return (
    <Page>
      <div className="p-8 flex justify-center items-center">
        <div className="flex justify-center items-center border border-muted-foreground rounded-lg aspect-[16/10] w-full max-w-[175px] bg-black shadow-2xl">
          <img
            src="/logo/vertical/profound-vertical-w.png"
            className="w-[90px]"
            alt="Card"
          />
        </div>
      </div>
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card
              className={cn(
                card.data.mode === CARD_MODES.PROFILE &&
                  'border-2 border-secondary scale-105 transition-transform duration-300 ease-in-out transform'
              )}
              onClick={() => handleSelectMode(CARD_MODES.PROFILE)}
            >
              <CardContent className="p-4 text-center flex flex-col justify-center items-center gap-2">
                <h3 className="text-lg font-bold">Profile</h3>
                <SquareUser />
              </CardContent>
            </Card>

            <Card
              className={cn(
                card.data.mode === CARD_MODES.REVIEW &&
                  'border-2 border-secondary scale-105 transition-transform duration-300 ease-in-out transform'
              )}
              onClick={() => handleSelectMode(CARD_MODES.REVIEW)}
            >
              <CardContent className="p-4 text-center flex flex-col justify-center items-center gap-2   ">
                <h3 className="text-lg font-bold">Review</h3>
                <Star />
              </CardContent>
            </Card>
            <Card
              className={cn(
                card.data.mode === CARD_MODES.CART &&
                  'border-2 border-secondary scale-105 transition-transform duration-300 ease-in-out transform'
              )}
              onClick={() => handleSelectMode(CARD_MODES.CART)}
            >
              <CardContent className="p-4 text-center flex flex-col justify-center items-center gap-2">
                <h3 className="text-lg font-bold">Cart</h3>
                <ShoppingCart />
              </CardContent>
            </Card>
          </div>
          {apiError && <Error error={apiError} />}
          {card.data.mode === CARD_MODES.PROFILE && (
            <Alert>
              <SquareUser size={18} />
              <AlertTitle>Profile Mode</AlertTitle>
              <AlertDescription>
                Your card is currently in Profile mode. This means when you tap
                your card on someones phone, it will redirect them to your
                profile
              </AlertDescription>
            </Alert>
          )}
          {card.data.mode === CARD_MODES.REVIEW && (
            <Alert>
              <Star size={18} />
              <AlertTitle>Review Mode</AlertTitle>
              <AlertDescription>
                Your card is currently in Review mode. This means when you tap
                your card on someones phone, it will redirect them to your
                review page.
              </AlertDescription>
            </Alert>
          )}
          {card.data.mode === CARD_MODES.CART && <ListCartsForCardPage />}
        </CardContent>
      </Card>
      <div className="p-12"></div>
    </Page>
  );
};

export default CardPage;
